import { Badge, Box, Flex, Heading, SimpleGrid, Text, useDisclosure } from '@chakra-ui/react';
import { useActivityInformationTravellersQuery } from '../api/useActivityInformationTravellersQuery';
import { InformationAlertDisplay } from '../shared/informationAlertDisplay';
import { faBank, faBuilding, faReceipt, faStar, faUmbrellaBeach } from '@fortawesome/free-solid-svg-icons';
import { userColorSchemes } from '../../../../constants/activityColorSchemes';
import { BaseCard, BaseDialog, BaseIcon, BaseSkeletonStack } from '@cks-internal/react-base';
import { ActivityInformationTravellerContactInfoBar } from '../shared/activityInformationTravellerContactInfoBar';
import { EntityNotesWarning } from '../../../../features/entityNotes/EntityNotesWarning';
import { EntityNoteQueryType } from '../../../../enums/common/EntityNoteQueryType';
import { EntityNoteType } from '../../../../enums/common/EntityNoteType';

import { EntityNotesDisplay } from '../../../../features/entityNotes/EntityNotesDisplay';
import { useEntityNotesQuery } from '../../../../features/entityNotes/apis/useEntityNotesQuery';
import { FooterFlsqBadge } from '../shared/FooterFlsqBadge';
import { BookingAuthorizationAlert } from '../shared/BookingAuthorizationAlert';
import { EntityNoteViewer } from '../shared/EntityNoteViewer';
import { useActivityInformationCompanyQuery } from '../api/useActivityInformationCompanyQuery';
import { useCompanyApprovalConfigQuery } from 'src/features/company/approvals/apis/useCompanyApprovalConfigQuery';
import { TravellerApprovalOverrideInfo } from '../../editActivity/approvals/components/TravellerApprovalOverrideInfo';
import { CompanyStatus } from '../../../../globalModels/common/companyStatus.ts';
import { CompanyContactType } from 'src/enums/crm/CompanyContactType.ts';
import { GroupsOnlyTravellerBadge } from '../../../../components/Common/badges/GroupsOnlyTravellerBadge';

// Component for displaying activity context enhanced notes with dialog
const ActivityContextNotes = ({ travellerId }: { travellerId: number }) => {
  const notesDialogDisclosure = useDisclosure();

  // Query to check if notes exist
  const notesQuery = useEntityNotesQuery(
    {
      EntityNoteQueryType: EntityNoteQueryType.Contact,
      Active: true,
      EntityNoteTypes: [EntityNoteType.AgentNote],
      IsPinned: true
    },
    travellerId
  );

  // Don't render anything if no notes exist
  if (!notesQuery.data || notesQuery.data.length === 0) {
    return null;
  }

  return (
    <>
      <Box cursor='pointer' onClick={notesDialogDisclosure.onOpen} _hover={{ opacity: 0.8 }}>
        <InformationAlertDisplay
          toDisplay={['Click to view all traveller notes']}
          status={'info'}
          header={'Traveller Notes'}
        />
      </Box>

      {/* Draggable Notes Dialog */}
      <BaseDialog
        dragDialog={{
          initialW: 600,
          initialH: 500
        }}
        disclosure={notesDialogDisclosure}
        header={<Heading>Traveller Notes</Heading>}>
        <EntityNotesDisplay
          entityId={travellerId}
          queryType={EntityNoteQueryType.Contact}
          noteDisplayTypes={[EntityNoteType.AgentNote]}
          isReadOnly={true}
          isPinned
        />
      </BaseDialog>
    </>
  );
};

type ActivityInformationTravellerDisplayProps = {
  companyId: number | undefined;
  travellerIds: number[];
  requestedById: number | undefined;
  approvedBookerId: number | undefined;
  displayCompany?: boolean;
  columns?: number;
  activityId?: number;
  flsq?: number;
};

/**
 * ActivityInformationTravellerDisplay component fetches and renders traveler-specific activity information.
 *
 * Responsibilities:
 * - Queries traveler details using `useActivityInformationTravellersQuery` with provided traveler and booker IDs.
 * - Displays a loading skeleton while the query is in progress.
 * - Renders nothing if no data is returned.
 * - Once data is available, shows a card (`BaseCard`) titled "Travelers" containing:
 *   - A Code Red warning via `EntityNotesWarning` for any traveler IDs.
 *   - A two-column grid of traveler cards, each displaying:
 *     - Traveler name, VIP/OBT/authorized-booker badges, and title.
 *     - Authorization status alert (allowed or not to book) with additional info.
 *     - Optional company display if `displayCompany` is true.
 *     - Contact info via `ActivityInformationTravellerContactInfoBar`.
 *     - Icons for branch office, preferred payment methods, and GDS card info.
 *     - Loyalty numbers list.
 *     - Informational alerts for Code Red notes, Agent Notes, and Personal Preferences.
 *
 * @component
 * @param {{
 *   travellerIds: number[];             // Array of traveler entity IDs to fetch
 *   approvedBookerId?: number;          // Optional ID for approved booker
 *   requestedById?: number;        // Optional ID for requested by contact
 *   displayCompany?: boolean;           // Flag to display company name under contact info
 * }} props
 * @returns {JSX.Element|null} The rendered traveler information card or null if no data.
 */

export const ActivityInformationTravellerDisplay = (props: ActivityInformationTravellerDisplayProps) => {
  const query = useActivityInformationTravellersQuery(
    [...props.travellerIds, props.approvedBookerId, props.requestedById].filter((val) => val != undefined)
  );

  const queryCompany = useActivityInformationCompanyQuery(props.companyId);
  const approvalConfigQuery = useCompanyApprovalConfigQuery(props.companyId);
  const hasAutomatedApprovalNotes: boolean = !!queryCompany.data?.AutomatedApprovalSystemNotes;

  // Activity context detection
  const isActivityContext = !!props.activityId;

  if (query.isLoading) {
    return <BaseSkeletonStack />;
  }
  if (!query.data) {
    return <></>;
  }

  return (
    <BaseCard
      size={'xs'}
      height={'fit-content'}
      w={'100%'}
      header={
        <Flex p={2} alignItems='center' justifyContent='space-between'>
          <Heading size='md'>Activity Travelers</Heading>
          <FooterFlsqBadge flsq={props.flsq} />
        </Flex>
      }>
      {props.travellerIds && props.travellerIds?.length > 0 && (
        <EntityNotesWarning
          queryType={EntityNoteQueryType.Contact}
          noteType={EntityNoteType.CodeRed}
          entityIds={props.travellerIds}
        />
      )}
      <SimpleGrid columns={props.columns ? props.columns : 2} gap={'8px'}>
        {query.data.map((val, key) => {
          // Get the corresponding traveller ID from the combined array
          const allIds = [...props.travellerIds, props.approvedBookerId, props.requestedById].filter(
            (val) => val != undefined
          );
          const travellerId = allIds[key];
          const isGroupsOnlyTraveller = val.CompanyContactType === CompanyContactType.GroupsOnlyTraveller;
          const isTravellerInactive = val.IsDisabled;
          const isCompanyDisabled = val.CompanyStatus === CompanyStatus.Disabled;
          const isCompanyImplementation = val.CompanyStatus === CompanyStatus.Implementation;
          const isDisabled = isTravellerInactive || isCompanyDisabled;
          return (
            <BaseCard
              variant={'subtle'}
              key={key}
              opacity={isDisabled ? 0.6 : 1}
              borderColor={isDisabled ? 'red.300' : undefined}
              borderWidth={isDisabled ? '1px' : undefined}
              header={
                <Flex flexDir={'column'}>
                  <Heading paddingTop={0} marginTop={0}>
                    <Flex gap={'4px'} whiteSpace={'normal'} flexWrap={'wrap'}>
                      {val.FirstName} {val.LastName}
                      {val.IsVip && (
                        <Badge colorPalette={userColorSchemes.vip}>
                          <BaseIcon toolTip={'Is a VIP'} icon={faStar}>
                            VIP{' '}
                          </BaseIcon>
                        </Badge>
                      )}
                      {val.HasObtAccess && (
                        <Badge colorPalette={userColorSchemes.obt}>
                          OBT {val.IsObtSuperUser ? <>Super User</> : <>Access</>}
                        </Badge>
                      )}
                      {val.IsAuthorizedBooker && (
                        <Badge colorPalette={userColorSchemes.authBooker}>Authorized Booker</Badge>
                      )}
                      {isGroupsOnlyTraveller && <GroupsOnlyTravellerBadge />}
                      {isTravellerInactive && <Badge colorPalette={userColorSchemes.inactive}>Disabled</Badge>}
                      {isCompanyDisabled && (
                        <Badge colorPalette={userColorSchemes.inactiveCompany}>Company Disabled</Badge>
                      )}
                      {isCompanyImplementation && <Badge colorPalette='orange'>Company Implementation</Badge>}
                    </Flex>
                  </Heading>
                  {val.Title && (
                    <Text fontSize={'12px'} lineHeight={'10px'}>
                      {val.Title}
                    </Text>
                  )}
                </Flex>
              }
              w={'100%'}
              display={'inline-block'}>
              <>
                <BookingAuthorizationAlert
                  hasAutomatedApprovalNotes={hasAutomatedApprovalNotes}
                  automatedApprovalSystemNotes={queryCompany.data?.AutomatedApprovalSystemNotes}
                  traveller={val}
                />
                <TravellerApprovalOverrideInfo
                  config={approvalConfigQuery.data}
                  travellerId={val.Id}
                  travellerTags={val.Tags}
                />
                <Box> {props.displayCompany && <i>{val.CompanyName}</i>}</Box>
                <ActivityInformationTravellerContactInfoBar contact={val} />
                <Flex flexDir={'column'} alignItems={'flex-start'}>
                  {val.BranchOffice && (
                    <BaseIcon toolTip={'Branch Office'} icon={faBuilding}>
                      <Box> {val.BranchOffice}</Box>
                    </BaseIcon>
                  )}

                  {val.PreferredPaymentMethods && val.PreferredPaymentMethods.length > 0 && (
                    <BaseIcon toolTip={'Preferred Payment'} icon={faBank}>
                      <Box> {val.PreferredPaymentMethods.join(', ')}</Box>
                    </BaseIcon>
                  )}
                  {val.GdsCardLastFour && (
                    <BaseIcon toolTip={'GDS Card On File'} icon={faBank}>
                      <Box> GDS Card On File: {val.GdsCardLastFour}</Box>
                    </BaseIcon>
                  )}
                  {val.PersonalGdsCardLastFour && (
                    <BaseIcon toolTip={'Personal GDS Card'} icon={faUmbrellaBeach}>
                      <Box> Personal Travel Card On File: {val.GdsCardLastFour}</Box>
                    </BaseIcon>
                  )}
                  {val.LoyaltyNumbers &&
                    val.LoyaltyNumbers.map((val, key) => {
                      return (
                        <BaseIcon key={key} toolTip={'Loyalty Number'} icon={faReceipt}>
                          <Box>
                            {val.Type} - {val.AccountNumber}
                          </Box>
                        </BaseIcon>
                      );
                    })}
                </Flex>
                <Flex flexDir={'column'} gap={'4px'}>
                  <EntityNoteViewer notes={val.CodeRedNotes} status={'error'} />

                  <EntityNoteViewer notes={val.AgentNotes} status={'warning'} />

                  <InformationAlertDisplay
                    toDisplay={val.PersonalPreferences ? [val.PersonalPreferences] : []}
                    status={'info'}
                    header={'Personal Preferences'}
                  />

                  {/* Activity Context Enhanced Notes */}
                  {isActivityContext && travellerId && <ActivityContextNotes travellerId={travellerId} />}
                </Flex>
              </>
            </BaseCard>
          );
        })}
      </SimpleGrid>
    </BaseCard>
  );
};
